import { useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { useBulkGrantCredits } from '../hooks/useBulkGrantCredits';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  cohortId: string;
};

export default function BulkGrantCreditsModal({ isOpen, onClose, cohortId }: Props) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const mutation = useBulkGrantCredits();

  const isValid = amount > 0 && reason.trim().length > 0;

  const handleSubmit = async () => {
    try {
      const result = await mutation.mutateAsync({ cohortId, amount, reason });
      toast({
        title: translate('wallet_admin.bulk_grant_success'),
        description: translate('wallet_admin.bulk_grant_summary')
          .replace('{success}', String(result.successCount))
          .replace('{total}', String(result.totalUsers))
          + (result.errorCount > 0
            ? ` (${result.errorCount} ${translate('wallet_admin.errors')})`
            : ''),
        status: result.errorCount > 0 ? 'warning' : 'success',
        duration: 5000,
      });
      setAmount(0);
      setReason('');
      onClose();
    } catch (error: any) {
      toast({
        title: translate('wallet_admin.bulk_grant_error'),
        description: error?.message || translate('common.unknown_error'),
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleClose = () => {
    setAmount(0);
    setReason('');
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('wallet_admin.grant_credits')}
      onSubmit={handleSubmit}
      submitLabel={translate('wallet_admin.grant_credits')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={mutation.isLoading}
      isSubmitDisabled={!isValid}
    >
      <FormStack>
        <FormField
          type="number"
          label={translate('wallet_admin.amount')}
          value={amount}
          onChange={(val) => setAmount(isNaN(val) ? 0 : val)}
          min={0}
          step={1}
          numberInputProps={{ precision: 2 }}
        />

        <FormField
          type="text"
          label={translate('wallet_admin.reason')}
          value={reason}
          onChange={setReason}
          placeholder={translate('wallet_admin.reason_placeholder')}
          inputProps={{ maxLength: 255 }}
        />
      </FormStack>
    </FormModal>
  );
}
